import { LoaderPreview } from "./LoaderPreview";
import type { LoadFlags } from "../types";

/**
 * Variant B of the start / landing hero (activated via `?variant=b`).
 *
 * Same props and handlers as {@link StartOverlay} so the two are drop-in
 * interchangeable — only the headline, layout and CTA hierarchy differ. Where
 * variant A is a compact bottom-anchored status card, variant B is a centered
 * marketing-style hero: a bold one-line pitch, a feature strip, and a single
 * dominant "Launch flight" CTA with the utility actions demoted to a ghost row.
 */
export function StartOverlayVariantB({
  previewUrl,
  visible,
  ready,
  sceneFallback,
  canConfigure,
  loadFlags,
  error,
  canInstall,
  isIOS,
  onInstall,
  onAlign,
  onConfig,
  onRetryScene,
  onPlay,
  onWatchRoute,
}: {
  previewUrl: string;
  visible: boolean;
  ready: boolean;
  sceneFallback: boolean;
  canConfigure: boolean;
  loadFlags: LoadFlags;
  error?: string;
  canInstall: boolean;
  isIOS: boolean;
  onInstall: () => void;
  onAlign: () => void;
  onConfig: () => void;
  onRetryScene: () => void;
  onPlay: () => void;
  onWatchRoute: () => void;
}) {
  if (!visible) return null;
  const pending = [
    !loadFlags.manifest && "manifest",
    !loadFlags.trajectory && "route",
    !loadFlags.splat && !sceneFallback && "3D scene",
    !loadFlags.jet && "jet",
  ]
    .filter(Boolean)
    .join(", ");
  const status = sceneFallback
    ? "3D scene offline — fallback flight is ready."
    : error || (ready ? "Scene ready — cleared for takeoff." : `Loading ${pending || "assets"}...`);

  const primaryLabel = ready ? (sceneFallback ? "Launch fallback" : "Launch flight") : "Spinning up...";

  return (
    <div className={`startup startup--b ${ready ? "is-ready" : ""} ${error ? "is-error" : ""}`}>
      <LoaderPreview url={previewUrl} />
      <div className="startup-b">
        <div className="startup-b__eyebrow">Myworld Flight · WebGL</div>
        <h1 className="startup-b__headline">
          Fly real worlds.
          <span>No install. Just the sky.</span>
        </h1>
        <p className="startup-b__lede">
          Grab the throttle and soar through photoreal Gaussian-splat reconstructions of real
          places — streamed straight into your browser.
        </p>

        <ul className="startup-b__features" aria-hidden="true">
          <li>Photoreal splat scenes</li>
          <li>Rings &amp; time trials</li>
          <li>Multiplayer rooms</li>
        </ul>

        <div className="startup-b__status" data-state={ready ? "ready" : error ? "error" : "loading"}>
          {status}
        </div>
        <div className="startup-progress startup-b__progress" aria-hidden="true">
          <i />
        </div>

        <div className="startup-b__cta">
          <button
            type="button"
            className="startup-b__primary"
            onClick={onPlay}
            disabled={!ready}
          >
            {primaryLabel}
          </button>
          <button type="button" onClick={onWatchRoute} disabled={!ready}>
            Watch route
          </button>
        </div>

        <div className="startup-b__utility">
          <button type="button" onClick={onAlign} disabled={!canConfigure}>
            Align
          </button>
          <button type="button" onClick={onConfig} disabled={!canConfigure}>
            Config
          </button>
          {error && (
            <button type="button" onClick={onRetryScene}>
              Retry 3D
            </button>
          )}
          {canInstall && (
            <button type="button" onClick={onInstall}>
              Install app
            </button>
          )}
        </div>

        {isIOS && <span className="ios-note">Safari: Share, then Add to Home Screen.</span>}
      </div>
    </div>
  );
}
